import { BindingError } from "../BindingError.js";
import { MarkerComparator } from "./MarkerComparator.js";
import { RequestResponseNormalizer } from "./RequestResponseNormalizer.js";
import { NormalizedPersistedData } from "../accessorTree/NormalizedPersistedData.js";
import { assertNever } from "../utils/assertNever.js";
import { PlaceholderGenerator } from "../markers/PlaceholderGenerator.js";
import { QueryLanguage } from "../queryLanguage/QueryLanguage.js";
var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => {
  __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
  return value;
};
const emptyEntityIdSet = /* @__PURE__ */ new Set();
class TreeStore {
  constructor(_schema) {
    this._schema = _schema;
    __publicField(this, "entityStore", /* @__PURE__ */ new Map());
    __publicField(this, "entityRealmStore", /* @__PURE__ */ new Map());
    __publicField(this, "markerTrees", /* @__PURE__ */ new Map());
    __publicField(this, "subTreeStatesByRoot", /* @__PURE__ */ new Map());
    __publicField(this, "persistedData", new NormalizedPersistedData(/* @__PURE__ */ new Map(), /* @__PURE__ */ new Map()));
  }
  mergeInQueryResponse(response, markerTree) {
    RequestResponseNormalizer.mergeInQueryResponse(this.persistedData, response, markerTree);
  }
  mergeInMutationResponse(response, operations) {
    RequestResponseNormalizer.mergeInMutationResponse(this.persistedData, response, operations);
  }
  get persistedEntityData() {
    return this.persistedData.persistedEntityDataStore;
  }
  get subTreePersistedData() {
    return this.persistedData.subTreeDataStore;
  }
  get schema() {
    return this._schema;
  }
  getPathBackToParent(entityRealm) {
    const blueprint = entityRealm.blueprint;
    if (blueprint.parent === void 0) {
      return void 0;
    }
    let parentEntityName;
    let relationFromParent;
    let parent;
    if (blueprint.type === "hasOne") {
      parentEntityName = blueprint.parent.entity.entityName;
      relationFromParent = blueprint.marker.parameters.field;
      parent = blueprint.parent;
    } else if (blueprint.type === "listEntity") {
      const grandparentBlueprint = blueprint.parent.blueprint;
      if (grandparentBlueprint.parent === void 0) {
        return void 0;
      }
      parentEntityName = grandparentBlueprint.parent.entity.entityName;
      relationFromParent = grandparentBlueprint.marker.parameters.field;
      parent = grandparentBlueprint.parent;
    } else {
      return assertNever();
    }
    const relationSchema = this.schema.getEntityRelation(parentEntityName, relationFromParent);
    const fieldBack = (relationSchema.ownedBy || relationSchema.inversedBy) ?? null;
    if (fieldBack === null) {
      return void 0;
    }
    return {
      parent,
      fieldBackToParent: fieldBack
    };
  }
  getSubTreeState(mode, treeRootId, aliasOrParameters, environment) {
    const markerRoot = this.markerTrees.get(treeRootId);
    const subTreeStates = this.subTreeStatesByRoot.get(treeRootId);
    if (subTreeStates === void 0 || markerRoot === void 0) {
      if (treeRootId) {
        throw new BindingError(
          `Invalid tree id '${treeRootId}'. Make sure you use the one supplied by the correct extendTree call.`
        );
      }
      throw new BindingError(`Failed to retrieve a sub tree.`);
    }
    let placeholderName;
    if (typeof aliasOrParameters === "string") {
      const placeholderByAlias = markerRoot.placeholdersByAliases.get(aliasOrParameters);
      if (placeholderByAlias === void 0) {
        throw new BindingError(`Undefined sub-tree alias '${aliasOrParameters}'.`);
      }
      placeholderName = placeholderByAlias;
    } else if (mode === "entityList") {
      placeholderName = PlaceholderGenerator.getEntityListSubTreePlaceholder(
        aliasOrParameters.isCreating ? QueryLanguage.desugarUnconstrainedQualifiedEntityList(
          aliasOrParameters,
          environment
        ) : QueryLanguage.desugarQualifiedEntityList(aliasOrParameters, environment),
        environment
      );
    } else if (mode === "entity") {
      placeholderName = PlaceholderGenerator.getEntitySubTreePlaceholder(
        aliasOrParameters.isCreating ? QueryLanguage.desugarUnconstrainedQualifiedSingleEntity(
          aliasOrParameters,
          environment
        ) : QueryLanguage.desugarQualifiedSingleEntity(aliasOrParameters, environment),
        environment
      );
    } else {
      throw new BindingError();
    }
    const subTreeState = subTreeStates.get(placeholderName);
    if (subTreeState === void 0) {
      throw new BindingError(`Trying to retrieve a non-existent sub-tree '${placeholderName}'.`);
    }
    return subTreeState;
  }
  getEntityListPersistedIds(state) {
    const blueprint = state.blueprint;
    if (blueprint.parent) {
      const entityData = this.persistedEntityData.get(blueprint.parent.entity.id.uniqueValue);
      return entityData?.get(blueprint.marker.placeholderName) ?? emptyEntityIdSet;
    } else {
      return this.subTreePersistedData.get(blueprint.marker.placeholderName) ?? emptyEntityIdSet;
    }
  }
  disposeOfRealm(realmToDisposeOf) {
    this.entityRealmStore.delete(realmToDisposeOf.realmKey);
    realmToDisposeOf.entity.realms.delete(realmToDisposeOf.realmKey);
    if (realmToDisposeOf.type === "entityRealm") {
      realmToDisposeOf.blueprint.parent?.childrenWithPendingUpdates?.delete(realmToDisposeOf);
      for (const child of realmToDisposeOf.children.values()) {
        switch (child.type) {
          case "field":
            continue;
          case "entityRealm":
          case "entityRealmStub": {
            this.disposeOfRealm(child);
            break;
          }
          case "entityList": {
            for (const listChild of child.children.values()) {
              this.disposeOfRealm(listChild);
            }
          }
        }
      }
    }
    const entity = realmToDisposeOf.entity;
    if (entity.realms.size === 0) {
      this.disposeOfEntity(entity);
    }
  }
  disposeOfEntity(entity) {
    for (const realm of entity.realms.values()) {
      this.disposeOfRealm(realm);
    }
    this.entityStore.delete(entity.id.uniqueValue);
    this.persistedEntityData.delete(entity.id.uniqueValue);
  }
  effectivelyHasTreeRoot(candidateRoot) {
    candidateRoots:
      for (const candidateSubTree of candidateRoot.subTrees.values()) {
        if (candidateSubTree.parameters.isCreating) {
          continue;
        }
        for (const root of this.markerTrees.values()) {
          for (const alreadyPresentSubTree of root.subTrees.values()) {
            const isSubset = MarkerComparator.isSubTreeSubsetOf(candidateSubTree, alreadyPresentSubTree);
            if (isSubset) {
              continue candidateRoots;
            }
          }
        }
        return false;
      }
    return true;
  }
}
export {
  TreeStore
};
//# sourceMappingURL=TreeStore.js.map
